/**
 * Where a tapped NFL push should land.
 *
 * Pure: (type, data) -> route | null. No IO, no imports that touch the network, so the
 * whole map is unit-testable without a device.
 *
 * ── null MEANS "FALL THROUGH", NOT "ERROR" ──────────────────────────────────
 *
 * Returning null hands the decision back to the existing landing chain
 * (landingChain.ts), which routes by DATA — active NFL league, in-season MLB league, and
 * so on. That is the current behaviour and it is a reasonable place to land.
 *
 * Several notification types deliberately return null because they have no honest
 * destination (see NO_DESTINATION below). A wrong-but-confident route is worse than the
 * status quo: the user arrives somewhere plausible, goes looking for the thing the
 * notification promised, and does not find it. Landing on the league home at least does
 * not make a claim.
 *
 * ── WHY league_id IS REQUIRED FOR LEAGUE-SCOPED ROUTES ──────────────────────
 *
 * Every route below is a tab within one league's shell. The shell renders whichever league
 * is active (sf_active_nfl_league_id, SAB-577), NOT the league in the URL — there is no
 * league id in the path. So the caller MUST set the active league from data.league_id
 * before navigating, or a push about League B renders League A's page under League B's
 * headline. This module returns null when league_id is absent precisely so that cannot
 * happen silently.
 */

/**
 * The NFL shell. Tabs are query params on this one route.
 *
 * ROUTING CUTOVER, rule (e). This deliberately points at the CANONICAL /nfl and not at a
 * room. The shell decides which draft room to render from the league's draft_status, so a
 * push about a league that is mid-draft lands in the OLD room and one about a pending or
 * finished league lands in the new one -- per league, at tap time, with no fetch in this
 * module and no draft-status parameter threaded through it.
 *
 * That only holds because handleNotificationTap sets the active league BEFORE navigating
 * (see nflPushDeepLink). Reverse those two and the shell judges the room on whichever
 * league happened to be active before the tap, which is how a user ends up in the new room
 * mid-draft on someone else's league. The ordering test in nflNotificationRoutes.test.ts
 * is what holds that in place.
 */
export const NFL_SHELL = '/nfl';

export type NflNotificationType = string;

export interface NotificationData {
  type?: string;
  league_id?: string;
  [k: string]: unknown;
}

/**
 * type -> tab within the league shell.
 *
 * DRAFT-TIME EVENTS ALL GO TO THE DRAFT ROOM, including pauses and sabotages. A pause
 * notification used to be a candidate for league home, but the draft room now renders the
 * paused state correctly (#288: it reads settings.draft_paused_at, not just draft_status),
 * so the draft room is where a pause is actually legible.
 */
export const TAB_BY_TYPE: Record<string, string> = {
  // ── Draft room ──
  draft_started: 'draft',
  draft_turn: 'draft',
  draft_pause: 'draft',
  autodraft: 'draft',
  auto_draft_all: 'draft',
  draft_undo: 'draft',
  skip_pick: 'draft',
  reverse_order: 'draft',
  sabotage: 'draft',
  sabotage_played: 'draft',
  sabotage_league: 'draft',

  // ── League home ──
  draft_cancel: 'home',
  league_full: 'home',
  member_joined: 'home',

  // ── Trades ──
  trade_proposed: 'trades',
  trade_accepted: 'trades',
  trade_declined: 'trades',
  trade_countered: 'trades',
  trade_vetoed: 'trades',
  trade_executed: 'trades',
  trade_completed: 'trades',
  trade_in: 'trades',
  trade_out: 'trades',

  // ── Roster / waivers ──
  waiver: 'team',
  waiver_won: 'team',
  waiver_lost: 'team',
  waiver_outbid: 'team',
  lineup_reminder: 'team',

  // ── Matchups / season ──
  matchup_result: 'matchups',
  championship_won: 'standings',
};

/**
 * Types with NO honest destination.
 *
 * NOTE ON REDUNDANCY: none of these appear in TAB_BY_TYPE, so the runtime check in
 * routeForNotification is DEFENCE IN DEPTH rather than live behaviour today -- a mutation
 * run confirmed that deleting the check breaks nothing. It is kept, and paired with an
 * invariant test that the two never overlap, so that a future edit adding e.g.
 * `draft_complete: 'draft'` to the map is caught rather than silently routing a type we
 * decided has nowhere to go.
 */
export const NO_DESTINATION: ReadonlySet<string> = new Set([
  // The recap is a VIEW STATE inside the draft tab, not addressable by URL. Sending a user
  // to the draft tab post-completion shows them the board, not the recap they were told about.
  'draft_complete',
  // A digest spanning multiple leagues. No single league shell is correct.
  'weekly',
  // No surface was found that displays this.
  'commissioner_reward',
  // An error with no page that explains why it failed.
  'trade_failed',
  // The roster tab shows the resulting roster but not the transaction that prompted the push.
  'add',
  'drop',
  'add_drop',
]);

/**
 * The route for a tapped notification, or null to fall through to the landing chain.
 *
 * Returns null when:
 *   - the type is unknown (a new type added server-side before this map learns it)
 *   - the type is deliberately unrouted (NO_DESTINATION)
 *   - league_id is missing, since every route here is league-scoped
 */
export function routeForNotification(data: NotificationData | null | undefined): string | null {
  const type = typeof data?.type === 'string' ? data.type : '';
  if (!type) return null;
  if (NO_DESTINATION.has(type)) return null;

  const tab = TAB_BY_TYPE[type];
  if (!tab) return null;

  // League-scoped without a league is not routable — see the module header.
  const leagueId = typeof data?.league_id === 'string' ? data.league_id.trim() : '';
  if (!leagueId) return null;

  return `${NFL_SHELL}?tab=${encodeURIComponent(tab)}`;
}

/**
 * The league a tapped notification refers to, or null.
 *
 * Separate from routeForNotification because the caller must set the active league BEFORE
 * navigating, and the two answers are needed at different moments.
 */
export function leagueIdForNotification(data: NotificationData | null | undefined): string | null {
  const leagueId = typeof data?.league_id === 'string' ? data.league_id.trim() : '';
  return leagueId || null;
}
